// Configuración del servicio API
const API_URL = process.env.NODE_ENV === 'production'
  ? 'https://dbtarifario.tnf.cl/api'  // URL de producción
  : 'http://localhost:5000/api';         // URL de desarrollo

// Función para obtener el tenant actual
const getCurrentTenant = () => {
  return localStorage.getItem('currentTenant') || 'chirakChile';
};

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  // Método para hacer peticiones HTTP
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken'); // Obtener token en cada petición
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': getCurrentTenant(), // Agregar header de tenant
        ...options.headers,
      },
      ...options,
    };

    // Agregar token de autenticación si existe
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // Método GET
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // Método POST
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Método PUT
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Método DELETE
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Configurar token de autenticación
  setToken(token) {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  // ============= MÉTODOS DE AUTENTICACIÓN =============

  async login(email, password, company) {
    const response = await this.post('/auth/login', { 
      email, 
      password, 
      company 
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async logout() {
    this.setToken(null);
    return { success: true };
  }

  async verifyToken() {
    try {
      return await this.get('/auth/verify');
    } catch (error) {
      this.setToken(null);
      throw error;
    }
  }

  // ============= MÉTODOS DE USUARIOS =============

  async getUsers() {
    return this.get('/users');
  }

  async getUserById(id) {
    return this.get(`/users/${id}`);
  }

  async createUser(userData) {
    return this.post('/users', userData);
  }

  async updateUser(id, userData) {
    return this.put(`/users/${id}`, userData);
  }

  async deleteUser(id) {
    return this.delete(`/users/${id}`);
  }

  // ============= MÉTODOS DE EMPRESAS =============

  async getCompanies() {
    return this.get('/companies');
  }

  async getCompanyById(id) {
    return this.get(`/companies/${id}`);
  }

  async createCompany(companyData) {
    return this.post('/companies', companyData);
  }

  async updateCompany(id, companyData) {
    return this.put(`/companies/${id}`, companyData);
  }

  async deleteCompany(id) {
    return this.delete(`/companies/${id}`);
  }

  // ============= MÉTODOS DE CATEGORÍAS =============

  async getCategories() {
    return this.get('/categories');
  }

  async getCategoryById(id) {
    return this.get(`/categories/${id}`);
  }

  async createCategory(categoryData) {
    return this.post('/categories', categoryData);
  }

  async updateCategory(id, categoryData) {
    return this.put(`/categories/${id}`, categoryData);
  }

  async deleteCategory(id) {
    return this.delete(`/categories/${id}`);
  }

  // ============= MÉTODOS DE PRODUCTOS =============

  async getProducts() {
    return this.get('/products');
  }

  async getProductById(id) {
    return this.get(`/products/${id}`);
  }

  async getProductsByCategory(categoryId) {
    return this.get(`/products/category/${categoryId}`);
  }

  async createProduct(productData) {
    return this.post('/products', productData);
  }

  async updateProduct(id, productData) {
    return this.put(`/products/${id}`, productData);
  }

  async deleteProduct(id) {
    return this.delete(`/products/${id}`);
  }

  // ============= MÉTODOS DE CONFIGURACIÓN =============

  async getSettings() {
    return this.get('/settings');
  }

  async updateSettings(settingsData) {
    return this.put('/settings', settingsData);
  }

  // ============= MÉTODOS DE ESTADÍSTICAS =============

  async getStats() {
    return this.get('/stats');
  }

  async getSalesStatistics(period = '30d') {
    return this.get(`/statistics/sales?period=${period}`);
  }

  async getAccessHistory(limit = 100) {
    return this.get(`/statistics/access?limit=${limit}`);
  }

  async getEditHistory(limit = 100) {
    return this.get(`/statistics/edits?limit=${limit}`);
  }

  // ============= MÉTODOS DE DESCARGAS =============

  async trackDownload(productId, documentName, documentUrl) {
    return this.post('/downloads/track', {
      product_id: productId,
      document_name: documentName,
      document_url: documentUrl
    });
  }

  async getDownloadStats(days = 30, limit = 10, page = 1, months = null) {
    let url = `/downloads/stats?limit=${limit}&page=${page}`;
    if (months) {
      url += `&months=${months}`;
    } else {
      url += `&days=${days}`;
    }
    return this.get(url);
  }

  async getDownloadStatsAll({ days = 30, months = null } = {}) {
    const params = months ? `months=${months}` : `days=${days}`;
    return this.get(`/downloads/stats/all?${params}`);
  }

  async getDownloadEvolution(days = 30) {
    return this.get(`/downloads/evolution?days=${days}`);
  }
}

// Exportar instancia única del servicio
export const apiService = new ApiService();
export default apiService;